<template>
  <div class="orders">
    <div class="orders__top">
      <p>{{ t('profile.orders.product') }}</p>
      <p>{{ t('profile.orders.status') }}</p>
      <p>{{ t('profile.orders.date') }}</p>
      <p>{{ t('profile.orders.file') }}</p>
    </div>
    <div class="orders__order" v-for="order in orders" :key="order.node.uuid">
      <div class="orders__product" v-for="product in order.node.orderProducts.edges" :key="product.node.product.uuid">
        <div class="orders__product-top">
          <img class="orders__product-image" :src="product.node.product.images.edges[0].node.image" :alt="product.node.product.name">
          <div class="orders__product-main">
            <div>
              <h6>{{ t('profile.orders.status') }}: </h6>
              <p>{{ order.node.status }}</p>
            </div>
            <div>
              <h6>{{ t('profile.orders.date') }}: </h6>
              <p>{{ dayjs(order.node.buyTime).format('MM/D/YY HH:mm') }}</p>
            </div>
            <div>
              <a :href="product.node.downloadUrl" :download="product.node.downloadUrl" v-if="order.node.status === 'FINISHED'">
                <svg width="31" height="31" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M11.4668 14.2775V21.9176L14.0135 19.3709" stroke="#171717" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M11.4666 21.9177L8.91992 19.371" stroke="#171717" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M28.0205 13.0043V19.371C28.0205 25.7377 25.4739 28.2844 19.1072 28.2844H11.4671C5.10039 28.2844 2.55371 25.7377 2.55371 19.371V11.731C2.55371 5.36425 5.10039 2.81757 11.4671 2.81757H17.8338" stroke="#171717" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M28.0207 13.0044H22.9274C19.1073 13.0044 17.834 11.731 17.834 7.91099V2.81763L28.0207 13.0044Z" stroke="#171717" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </a>
              <svg v-else class="not" width="31" height="31" viewBox="0 0 31 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.4668 14.2775V21.9176L14.0135 19.3709" stroke="#171717" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M11.4666 21.9177L8.91992 19.371" stroke="#171717" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M28.0205 13.0043V19.371C28.0205 25.7377 25.4739 28.2844 19.1072 28.2844H11.4671C5.10039 28.2844 2.55371 25.7377 2.55371 19.371V11.731C2.55371 5.36425 5.10039 2.81757 11.4671 2.81757H17.8338" stroke="#171717" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M28.0207 13.0044H22.9274C19.1073 13.0044 17.834 11.731 17.834 7.91099V2.81763L28.0207 13.0044Z" stroke="#171717" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>
        </div>
        <div class="orders__product-bottom">
          <p class="orders__product-name">{{ product.node.product.name }}</p>
          <div class="orders__rate">
            <div class="orders__rate-inner">
              <el-rate
                  v-model="productRatings[product.node.uuid]"
                  :disabled="!!product.node.feedback?.rating"
                  @change="setRating(product.node.uuid, productRatings[product.node.uuid])"
              />
              {{ productRatings[product.node.uuid] || 0 }}
            </div>
            <p>{{ t('profile.orders.rate') }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {Orders} from "@/core/api/orders.js";
import {computed, onMounted, reactive, ref} from "vue";
import {useAuthStore} from "@/stores/auth.js";
import {useI18n} from "vue-i18n";
import dayjs from "dayjs";
import {Feedbacks} from "@/core/api/feedbacks.js";
import {ElNotification} from "element-plus";

const {t} = useI18n()
const authStore = useAuthStore()

const userEmail = computed(() => {
  return authStore.user ? authStore.user?.email : null
})
const accessToken = computed(() => {
  return authStore.accessToken ? authStore.accessToken : null
})

const isLoading = ref(true)
const orders = ref([])
const productRatings = reactive({})

const getOrders = async () => {
  isLoading.value = true

  const res = await Orders.getOrders("", accessToken.value, userEmail.value)
  orders.value = res.data.orders?.edges
      .filter((order) => order.node.status !== 'PENDING')

  orders.value.forEach(order => {
    order.node.orderProducts.edges.forEach(product => {
      if (!(product.node.uuid in productRatings)) {
        productRatings[product.node.uuid] = product.node.feedback?.rating || 0
      }
    })
  })

  isLoading.value = false
}

const setRating = async (orderProductUuid, rating) => {
  const res = await Feedbacks.setFeedback(
      accessToken.value,
      'add',
      orderProductUuid,
      rating
  )

  if (res.errors) {
    ElNotification({
      message: res.errors[0].message,
      type: 'error'
    })
  } else {
    ElNotification({
      message: t('popup.rating'),
      type: 'success'
    })
  }
}

onMounted( async () => {
  await getOrders()
});
</script>

<style lang="scss" scoped>
.orders {
  width: 100%;
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 35px;

  &__top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-inline: 30px;
    border-bottom: 2px solid #5B194F;
    padding-bottom: 10px;

    & p {
      width: 20%;

      color: #696969;
      text-align: center;
      font-size: 16px;
      font-weight: 600;

      &:first-child {
        width: 40% !important;
      }
    }
  }

  &__order {
    display: flex;
    flex-direction: column;
    gap: 15px;
    border-bottom: 1px solid #B1B1B1;
    padding-bottom: 25px;
  }

  &__product {
    display: flex;
    flex-direction: column;

    &-top {
      display: flex;
      align-items: center;
    }

    &-bottom {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    &-image {
      width: 100%;
      max-width: 268px;
      height: 226px;
      object-fit: cover;
      border-radius: 20px 20px 0 0;
    }

    &-name {
      max-width: 100%;
      width: fit-content;
      margin-top: 20px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      color: #2B3044;
      font-family: Syne, sans-serif;
      font-size: 18px;
      font-weight: 700;
    }

    &-main {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;

      & div {
        width: 33%;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;

        & h6 {
          display: none;

          color: #2B3044;
          text-align: center;
          font-size: 20px;
          font-weight: 600;
        }

        & p {
          color: #2B3044;
          text-align: center;
          font-size: 16px;
          font-weight: 500;
        }

        & a {
          & svg {
            &.not {
              cursor: not-allowed !important;
            }
          }
        }
      }
    }
  }

  &__rate {
    display: flex;
    align-items: center;
    gap: 20px;
    border-radius: 8px;
    border: 1px solid #5B194F;
    width: fit-content;
    padding: 9px;

    color: #2B3044;
    font-size: 14px;
    font-weight: 500;

    &-inner {
      display: flex;
      align-items: center;
      gap: 15px;
      padding-right: 20px;
      border-right: 1px solid #5B194F;
    }
  }
}

@media (max-width: 700px) {
  .orders {
    &__top {
      display: none;
    }

    &__product {
      gap: 25px;

      &-left {
        width: unset;
      }

      &-main {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;

        & div {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: flex-start;

          & h6 {
            display: block;
          }
        }
      }
    }
  }
}

@media (max-width: 550px) {
  .orders {
    &__product {
      flex-direction: column;

      &-left {
        width: 100%;
        align-items: center;
      }

      &-main {
        & div {
          justify-content: center;
        }
      }
    }
  }
}
</style>